import { FC, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  ConfirmationDialog,
  Button,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import PageContent from "@/pages/PageContent";
import { useRevokeConsolidationPriority } from "@/hooks/useConsolidation";
import {
  ConsolidationPriority,
  ConsolidationPriorityMode,
} from "@/models/consolidation";
import { MODE_LABELS } from "./consolidationPriorityDisplay";
import { DataTable } from "@/components/data-table/data-table";
import { logger } from "@/lib/logger";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import PrioritizeLawDialog from "./PrioritizeLawDialog";
import { PriorityColumns } from "./components/PriorityColumns";
import usePriorityTableData from "./hooks/usePriorityTableData";

/**
 * Admin settings view where admins prioritize laws (or parts of their
 * timeline) for consolidation. Prioritized scopes are consolidated
 * automatically and sorted first in the manual review process; this view also
 * shows their consolidation status (refreshed every 30s). Sorting, searching
 * and pagination follow the tone-of-voice settings table.
 *
 * Gated by the Consolidation-Prioritization Unleash flag and the
 * ManageConsolidationPriority permission at the route (see AppRouter).
 */
const ConsolidationPrioritySettingsPage: FC = () => {
  const {
    data,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    columnFilters,
    search,
    handleSearchChange,
    setPageIndex,
    setPageSize,
    setSorting,
    setColumnFilters,
    isLoading,
    isError,
    requesterOptions,
  } = usePriorityTableData();

  // Faceted filters (Omfang / Prioritet / Oprettet af), keyed by column id.
  const tableFilters = useMemo(
    () => ({
      mode: (Object.keys(MODE_LABELS) as ConsolidationPriorityMode[]).map(
        (mode) => ({ label: MODE_LABELS[mode], value: mode })
      ),
      level: [1, 2, 3, 4, 5].map((level) => ({
        label:
          level === 1
            ? "1 (højest)"
            : level === 5
              ? "5 (lavest)"
              : String(level),
        value: String(level),
      })),
      requestedByName: requesterOptions.map((name) => ({
        label: name,
        value: name,
      })),
    }),
    [requesterOptions]
  );
  const revokePriority = useRevokeConsolidationPriority();

  const [prioritizeOpen, setPrioritizeOpen] = useState(false);
  const [priorityToEdit, setPriorityToEdit] =
    useState<ConsolidationPriority | null>(null);
  const [priorityToRevoke, setPriorityToRevoke] =
    useState<ConsolidationPriority | null>(null);

  const handleRevoke = async () => {
    if (!priorityToRevoke) return;
    const revoked = priorityToRevoke;
    setPriorityToRevoke(null);
    try {
      await revokePriority.mutateAsync(revoked.id);
      showSuccessToast(
        `Prioriteringen af "${revoked.lawTitle ?? revoked.pivotExternalId}" er fjernet.`
      );
    } catch (error) {
      logger.error("Failed to revoke consolidation priority", {
        operation: "revokeConsolidationPriorityFromSettings",
        error,
        priorityId: revoked.id,
      });
      showErrorToast("Kunne ikke fjerne prioriteringen.");
    }
  };

  const handleRowClick = (rowIndex: number) => {
    const priority = data[rowIndex];
    if (priority) setPriorityToEdit(priority);
  };

  return (
    // overflow-hidden locks PageContent's own scroller: the page never
    // scrolls — only the table rows do (DataTable scrollRows), keeping the
    // header and the pagination footer always visible.
    <PageContent
      title="Konsolideringsprioritering"
      contentClassName="overflow-hidden"
    >
      <div className="flex h-full min-h-0 w-full flex-col space-y-2 p-4 xl:px-8">
        <div className="mb-4 flex shrink-0 items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">
              Konsolideringsprioritering
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Prioriterede love konsolideres automatisk med det samme og
              sorteres først i den manuelle godkendelsesproces. Vælg en lov og
              om prioriteringen gælder ét vindue, tidslinjen fremad, tidslinjen
              til og med dokumentet eller hele tidslinjen.
            </p>
          </div>
          <Button onClick={() => setPrioritizeOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Prioriter lov
          </Button>
        </div>

        {isLoading ? (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <Spinner className="size-12 text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="py-8 text-sm text-destructive">
            Prioriteringerne kunne ikke hentes. Prøv igen senere.
          </p>
        ) : (
          <div className="min-h-0 flex-1">
            <DataTable
              columns={PriorityColumns(setPriorityToRevoke, setPriorityToEdit)}
              data={data}
              pageCount={pageCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              sorting={sorting}
              search={search}
              handleSearchChange={handleSearchChange}
              setSorting={setSorting}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              filters={tableFilters}
              alwaysShowFilters={true}
              handleRowClick={handleRowClick}
              stickyHeader={true}
              scrollRows={true}
            />
          </div>
        )}
      </div>

      <PrioritizeLawDialog
        open={prioritizeOpen}
        onOpenChange={setPrioritizeOpen}
      />

      <PrioritizeLawDialog
        open={priorityToEdit !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPriorityToEdit(null);
        }}
        existingPriority={priorityToEdit}
      />

      <ConfirmationDialog
        isOpen={priorityToRevoke !== null}
        onClose={() => setPriorityToRevoke(null)}
        onConfirm={() => void handleRevoke()}
        title="Fjern prioritering?"
        description={`Konsolideringen af "${priorityToRevoke?.lawTitle ?? priorityToRevoke?.pivotExternalId}" falder tilbage til normal prioritet. Allerede udførte konsolideringer bevares.`}
        confirmText="Fjern prioritering"
      />
    </PageContent>
  );
};

export default ConsolidationPrioritySettingsPage;
